// Shared API host resolver for CDN mode
// Resolves API base URL by pinging candidate subdomains (e.g., api.example.com)
// Exposes: ready (Promise), getApiBaseUrl(), buildApiUrl(path), hasApi()

// Subdomains to try for API host (e.g., api.example.com)
const CANDIDATE_SUBDOMAINS = ['api'];
// Subdomain prefixes to strip from hostname before building API candidates
// (e.g., www.example.com → example.com → api.example.com)
const STRIP_SUBDOMAINS = ['www', 'cdn'];
let apiBaseUrl = null;
let apiResolved = false;

function stripKnownSubdomains(hostname) {
  for (const sub of STRIP_SUBDOMAINS) {
    if (hostname.startsWith(sub + '.')) {
      return hostname.slice(sub.length + 1);
    }
  }
  return hostname;
}

function buildCandidateBases() {
  const protocol = window.location.protocol || 'https:';
  const hostWithPort = window.location.host; // includes port in dev
  const hostname = window.location.hostname; // bare hostname for CDN
  const sameOrigin = `${protocol}//${hostWithPort}`;
  const baseHostname = stripKnownSubdomains(hostname);
  const cdnHosts = CANDIDATE_SUBDOMAINS.map((sub) => `${protocol}//${sub}.${baseHostname}`);
  // In localhost/dev, prefer same-origin so MSW SW can intercept requests.
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
    return [sameOrigin, ...cdnHosts];
  }
  return cdnHosts;
}

function ping(url, timeoutMs = 5000) {
  return new Promise((resolve) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    fetch(`${url}/ping`, { method: 'GET', cache: 'no-store', signal: controller.signal })
      .then((res) => {
        clearTimeout(id);
        resolve(res.ok);
      })
      .catch(() => {
        clearTimeout(id);
        resolve(false);
      });
  });
}

async function resolveApiBaseUrl() {
  if (apiResolved) return apiBaseUrl;
  const bases = buildCandidateBases();
  for (const base of bases) {
    const ok = await ping(base);
    if (ok) {
      apiBaseUrl = base;
      apiResolved = true;
      window.__apiBaseUrl = apiBaseUrl;
      return apiBaseUrl;
    }
  }
  apiResolved = true;
  apiBaseUrl = null;
  window.__apiBaseUrl = null;
  return null;
}

export function getApiBaseUrl() {
  return apiBaseUrl;
}

export function buildApiUrl(path) {
  if (!apiBaseUrl) return null;
  if (!path.startsWith('/')) return `${apiBaseUrl}/${path}`;
  return `${apiBaseUrl}${path}`;
}

export function hasApi() {
  return Boolean(apiBaseUrl);
}

async function trackVisitWithPageParams() {
  if (!apiBaseUrl) return null;
  const qs = window.location.search || '';
  const url = buildApiUrl(`/api/visit/track${qs || ''}`);
  try {
    const res = await fetch(url, { method: 'GET', credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    if (data && typeof data.visitId !== 'undefined') {
      window.__visitId = data.visitId;
    }
    if (data && typeof data.offerVisitIds !== 'undefined') {
      window.__visitData = Object.assign({}, window.__visitData || {}, { offerVisitIds: data.offerVisitIds });
    }
    // Keep any additional fields for future use and expose them to the frontend
    const { name, amount, restamount, mfoscore, fullamount } = data || {};
    const fields = { name: name ?? null, amount: typeof amount === 'number' ? amount : (amount ? Number(amount) : null), restamount: typeof restamount === 'number' ? restamount : (restamount ? Number(restamount) : null), mfoscore: typeof mfoscore === 'number' ? mfoscore : (mfoscore ? Number(mfoscore) : null), fullamount: typeof fullamount === 'number' ? fullamount : (fullamount ? Number(fullamount) : null) };
    window.__visitData = Object.assign({}, window.__visitData || {}, { fields });
    try {
      const evt = new CustomEvent('visit:tracked', { detail: { visitId: window.__visitId, offerVisitIds: window.__visitData.offerVisitIds, fields } });
      window.dispatchEvent(evt);
      document.dispatchEvent(evt);
    } catch (_) {}
    return data;
  } catch (_) {
    return null;
  }
}

export const ready = (async () => {
  // В dev дождёмся готовности MSW, если промис выставлен глобально, чтобы /ping перехватывался корректно
  try {
    if (typeof window !== 'undefined' && window.__mswReadyPromise && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.endsWith('.localhost'))) {
      await window.__mswReadyPromise.catch(() => {});
    }
  } catch (_) {}
  const base = await resolveApiBaseUrl();
  if (!base) {
    // No API hosts — switch to offline
    window.__workOffline = true;
    return null;
  }
  // Track visit before anything else
  await trackVisitWithPageParams();
  return base;
})();
